// src/pages/StudiosIndex.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Stack,
  Button,
  Chip,
  Card,
  CardContent,
  CardActionArea,
  CardActions,
  Avatar,
  Grid,
  Container,
  Skeleton,
  TextField,
  InputAdornment,
  Tooltip,
  Rating,
  Menu,
  MenuItem,
  Divider,
  Snackbar,
  Alert,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MicExternalOnIcon from "@mui/icons-material/MicExternalOn";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import TuneIcon from "@mui/icons-material/Tune";
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import { directoryService } from "../services/resources.service";
import { hasPermission, PERMISSIONS } from "../services/authRoles";
import { buildRecipientInboxPath } from "../services/messaging";
import { canContactStudio } from "../services/proFeatures";
import ProFeatureControl from "../components/ProFeatureControl";
import { useLocale, useMessage } from "../i18n/locale";
import { getStudioRateDisplay } from "../services/studioRates";
import DiscoveryMap from "../components/DiscoveryMap";
import { ViewModeToggle } from "../components/DiscoveryActions";
import { INFINITE_RADIUS, NEAR_ME_RADIUS_KM, radiusLabel } from "../services/radius";
import { getDirectoryLocationQuery } from "../services/eventDiscovery";
import LinkedAccountBadge from "../components/LinkedAccountBadge";
import { BANNER_IMAGE_FALLBACK, getBannerImage, imageFallbackHandler } from "../services/profileImages";
import { fetchAllMapEntities } from "../services/mapEntities";
import useDebouncedSearch from "../hooks/useDebouncedSearch";

/**
 * GET /studios?query&services[]=recording|mixing|mastering|rehearsal&rate=lt300|300-600|gt600|any
 * &ratingMin&radiusKm&city&page&pageSize
 */
async function fetchStudios({
  query,
  services,
  rate,
  ratingMin,
  radiusKm,
  city,
  page,
  pageSize,
  latitude,
  longitude,
}) {
  return directoryService.studios({ query, services, rate, ratingMin, radiusKm, city, page, pageSize, latitude, longitude });
}

// ---------------- UI bits ----------------
function RateFromChip({ studio, locale }) {
  return (
    <Chip
      size="small"
      icon={<PriceChangeIcon />}
      label={getStudioRateDisplay(studio, locale)}
      variant="outlined"
    />
  );
}

function StudioCard({ studio, onOpen, onContact, contactLocked, contactLabel, locale }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: "100%", display: "flex", flexDirection: "column" }}>
      <CardActionArea onClick={() => onOpen?.(studio)} sx={{ flexGrow: 1 }}>
        <img
          src={getBannerImage(studio)}
          onError={imageFallbackHandler(BANNER_IMAGE_FALLBACK)}
          alt={studio.name}
          loading="lazy"
          style={{ width: "100%", height: 140, objectFit: "cover", borderTopLeftRadius: 12, borderTopRightRadius: 12 }}
        />
        <CardContent>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="subtitle1" fontWeight={800} noWrap>{studio.name}</Typography>
            <LinkedAccountBadge ownerUserId={studio.ownerUserId} />
          </Stack>
          <Typography variant="body2" color="text.secondary" noWrap>
            {studio.city} • {studio.services.slice(0, 3).join(", ")}
            {studio.services.length > 3 ? "…" : ""}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: "wrap" }}>
            <Rating
              value={studio.rating || 0}
              precision={0.1}
              size="small"
              readOnly
              icon={<StarRoundedIcon fontSize="inherit" />}
              emptyIcon={<StarRoundedIcon fontSize="inherit" />}
            />
            <Typography variant="caption" color="text.secondary">
              {studio.rating?.toFixed?.(1) ?? "—"}
            </Typography>
            <RateFromChip studio={studio} locale={locale} />
          </Stack>
        </CardContent>
      </CardActionArea>
      <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
        <Button fullWidth variant="contained" onClick={() => onOpen?.(studio)}>
          View studio
        </Button>
        {onContact && (
          <ProFeatureControl locked={contactLocked} label={contactLabel}>
            <span>
              <Button fullWidth variant="outlined" disabled={contactLocked} onClick={() => onContact(studio)}>
                {contactLabel}
              </Button>
            </span>
          </ProFeatureControl>
        )}
      </CardActions>
    </Card>
  );
}

// ---------------- Page ----------------
export default function StudiosIndex({ onNavigate, onRequireAuth, onChangeRegion, discoveryLocation, discoveryRadiusKm, onChangeDiscoveryRadius, user, region = "Anywhere" }) {
  const { locale } = useLocale();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const contactLabel = useMessage("studioContact");
  const mayContactByRole = !user || hasPermission(user, PERMISSIONS.CONTACT_STUDIO);
  const contactLocked = Boolean(user) && mayContactByRole && !canContactStudio(user);

  // Filters
  const { inputValue: searchInput, setInputValue: setSearchInput, searchValue: query, submitSearch } = useDebouncedSearch();
  const [services, setServices] = useState(["All"]); // multi
  const [rate, setRate] = useState("any"); // lt300 | 300-600 | gt600 | any
  const [ratingMin, setRatingMin] = useState(0); // 0..5
  const [city, setCity] = useState(region);
  const radiusKm = discoveryRadiusKm;
  const setRadiusKm = onChangeDiscoveryRadius;
  const [mapMode, setMapMode] = useState(false);

  // Data
  const [studios, setStudios] = useState([]);
  const [mapStudios, setMapStudios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 12;
  const loadMoreRef = useRef(null);

  // UI
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "info" });

  useEffect(() => {
    let active = true;
    fetchAllMapEntities(directoryService.studios, {
      query,
      services,
      rate,
      ratingMin,
      ...getDirectoryLocationQuery(city, radiusKm, discoveryLocation),
    }).then((items) => {
      if (active) setMapStudios(items);
    });
    return () => { active = false; };
  }, [query, services, rate, ratingMin, city, radiusKm, discoveryLocation]);

  const serviceOptions = ["All", "Recording", "Mixing", "Mastering", "Rehearsal"];

  const runFetch = async ({ reset = false } = {}) => {
    setLoading(true);
    const nextPage = reset ? 1 : page;
    const res = await fetchStudios({
      query,
      services,
      rate,
      ratingMin,
      radiusKm,
      ...getDirectoryLocationQuery(city, radiusKm, discoveryLocation),
      page: nextPage,
      pageSize,
    });
    setLoading(false);
    setHasMore(res.hasMore);
    if (reset) {
      setPage(1);
      setStudios(res.data);
    } else {
      setStudios((prev) => [...prev, ...res.data]);
    }
  };

  useEffect(() => {
    runFetch({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, JSON.stringify(services), rate, ratingMin, city, radiusKm, discoveryLocation]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasMore || loading || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) target.click(); },
      { rootMargin: "240px 0px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, loading, page]);

  const toggleService = (svc) => {
    setServices((prev) => {
      if (svc === "All") return ["All"];
      const s = new Set(prev.filter((x) => x !== "All"));
      s.has(svc) ? s.delete(svc) : s.add(svc);
      return Array.from(s);
    });
  };

  const subtitle = useMemo(() => {
    const parts = [region];
    if (city !== "Anywhere") parts.push(city);
    parts.push(radiusLabel(radiusKm));
    if (services.length && !services.includes("All")) parts.push(services.join(", "));
    if (rate !== "any")
      parts.push(rate === "lt300" ? "< R300" : rate === "gt600" ? "> R600" : "R300–R600/hr");
    if (ratingMin) parts.push(`≥ ${ratingMin.toFixed(1)}★`);
    return parts.join(" • ");
  }, [region, city, radiusKm, services, rate, ratingMin]);

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "background.default" }}>
      {/* Page AppBar */}
      <AppBar position="sticky" elevation={0} color="inherit">
        <Toolbar sx={{ gap: 1 }}>
          <IconButton edge="start" aria-label="back" onClick={() => onNavigate?.("/")}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ fontWeight: 900, letterSpacing: 0.2 }}>
            Studios
          </Typography>

          <Box component="form" onSubmit={submitSearch} sx={{ flex: 1, mx: 1 }}>
            <TextField
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              size="small"
              fullWidth
              placeholder="Search studios"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Map/List toggle */}
          <ViewModeToggle
            mapMode={mapMode}
            onChange={setMapMode}
            sx={{ display: { xs: "none", sm: "inline-flex" }, mr: 1 }}
          />

          <Tooltip title="More filters">
            <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="filters">
              <TuneIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 2 }}>
        {/* Filter chips */}
        <Stack spacing={2} sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
            <Chip
              icon={<LocationOnIcon />}
              label={city}
              onClick={async () => {
                if (city === "Near me" && radiusKm !== INFINITE_RADIUS) {
                  setRadiusKm(INFINITE_RADIUS);
                  return;
                }
                if (discoveryLocation) {
                  setCity("Near me");
                  setRadiusKm(NEAR_ME_RADIUS_KM);
                  return;
                }
                const location = await onChangeRegion?.();
                if (location) {
                  setCity("Near me");
                  setRadiusKm(NEAR_ME_RADIUS_KM);
                }
              }}
              variant={city === "Near me" && radiusKm !== INFINITE_RADIUS ? "filled" : "outlined"}
            />

            {[2, 10, 25, 100, null].map((km) => (
              <Chip
                key={km ?? "infinite"}
                label={radiusLabel(km)}
                variant={radiusKm === km ? "filled" : "outlined"}
                onClick={() => setRadiusKm(km)}
                size="small"
              />
            ))}

            {serviceOptions.map((svc) => (
              <Chip
                key={svc}
                label={svc}
                icon={svc !== "All" ? <MicExternalOnIcon /> : undefined}
                variant={
                  services.includes(svc) || (svc === "All" && services.length === 1) ? "filled" : "outlined"
                }
                onClick={() => toggleService(svc)}
                size="small"
              />
            ))}

            {[
              { key: "any", label: "Any rate" },
              { key: "lt300", label: "< R300" },
              { key: "300-600", label: "R300–R600" },
              { key: "gt600", label: "> R600" },
            ].map((b) => (
              <Chip
                key={b.key}
                label={b.label}
                variant={rate === b.key ? "filled" : "outlined"}
                onClick={() => setRate(b.key)}
                size="small"
              />
            ))}

            {[0, 3.5, 4.0, 4.5].map((r) => (
              <Chip
                key={r}
                icon={<StarRoundedIcon />}
                label={r === 0 ? "All ratings" : `≥ ${r}`}
                variant={ratingMin === r ? "filled" : "outlined"}
                onClick={() => setRatingMin(r)}
                size="small"
              />
            ))}
          </Stack>

          <Typography variant="body2" color="text.secondary">
            {subtitle ? `Showing: ${subtitle}` : "Showing all studios"}
          </Typography>
        </Stack>

        {/* Map */}
        {mapMode && (
          <Box sx={{ mb: 2 }}>
            <DiscoveryMap
              items={mapStudios}
              getCoordinates={(studio) => studio.geo}
              getLabel={(studio) => studio.name}
              onSelect={(studio) => onNavigate?.(`/studios/${studio.id}`)}
              labelMessageKey="studioMapLabel"
              height={320}
            />
          </Box>
        )}

        {/* Grid */}
        <Grid container spacing={2}>
          {(loading && studios.length === 0 ? Array.from({ length: 8 }) : studios).map((s, idx) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={s?.id ?? idx}>
              {s ? (
                <StudioCard
                  locale={locale}
                  studio={s}
                  onOpen={(studio) => onNavigate?.(`/studios/${studio.id}`)}
                  onContact={mayContactByRole
                    ? (studio) => {
                      const redirectTo = buildRecipientInboxPath("studio", studio.id);
                      if (!user) return onRequireAuth?.({ redirectTo, reasonKey: "loginModalMessageReason" });
                      onNavigate?.(redirectTo);
                    }
                    : undefined}
                  contactLocked={contactLocked}
                  contactLabel={contactLabel}
                />
              ) : (
                <Skeleton variant="rounded" height={220} sx={{ borderRadius: 3 }} />
              )}
            </Grid>
          ))}
        </Grid>

        {/* Pagination */}
        <Stack alignItems="center" sx={{ py: 3 }}>
          {hasMore ? (
            <Button
              ref={loadMoreRef}
              size="large"
              disabled={loading}
              onClick={async () => {
                setPage((p) => p + 1);
                setLoading(true);
                const res = await fetchStudios({
                  query,
                  services,
                  rate,
                  ratingMin,
                  radiusKm,
                  city,
                  page: page + 1,
                  pageSize,
                });
                setLoading(false);
                setHasMore(res.hasMore);
                setStudios((prev) => [...prev, ...res.data]);
              }}
            >
              {loading ? "Loading…" : "Load more"}
            </Button>
          ) : (
            <Typography variant="body2" color="text.secondary">
              You’ve reached the end.
            </Typography>
          )}
        </Stack>
      </Container>

      {/* More filters menu (placeholder for future expansion) */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        MenuListProps={{ "aria-labelledby": "more-filters-button" }}
      >
        <MenuItem disabled>More filters coming soon…</MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setServices(["All"]);
            setRate("any");
            setRatingMin(0);
            setCity("Anywhere");
            setRadiusKm(INFINITE_RADIUS);
            setMenuAnchor(null);
          }}
        >
          Reset all
        </MenuItem>
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={2200}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>

      <Box sx={{ height: 16 }} />
    </Box>
  );
}
